import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';
import useLocale from '../hooks/useLocale';
import useUpdateBreadcrumbs from '../hooks/useUpdateBreadcrumbs';
import './ThesisManagement.css';

const ThesisElectionPreviewPage = () => {
  // was conference_id, section_id, thesis_id
  const { festschrift_id, section_id, article_id } = useParams();
  const { getCurrentLanguage, getLocalizedName } = useLocale();
  const lang = getCurrentLanguage();

  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Хлебные крошки: сборник → страница выборов → статья
  const resetBreadcrumbs = useCallback(
    (search) => {
      const searchParams = new URLSearchParams(search);
      const confName = searchParams.get('conferenceName') || '';
      const sectionName = searchParams.get('sectionName') || '';
      const thesisTitle = searchParams.get('thesisTitle') || '';

      return [
        { label: confName },
        {
          label: sectionName,
          url:
            `/publishing/${festschrift_id}/sections/${section_id}/election` +
            `?conferenceName=${encodeURIComponent(confName)}` +
            `&sectionName=${encodeURIComponent(sectionName)}`,
        },
        { label: thesisTitle },
      ];
    },
    [festschrift_id, section_id],
  );
  useUpdateBreadcrumbs(resetBreadcrumbs);

  useEffect(() => {
    const fetchArticleData = async () => {
      setLoading(true);
      try {
        // was /rates/{conference_id}/sections/{section_id}/thesises/{thesis_id}
        const response = await api.get(
          `/rates/publishing/${festschrift_id}/sections/${section_id}/articles/${article_id}`,
        );
        setArticleData(response.data);
        setError('');
      } catch (err) {
        console.error(err);
        setError(
          lang === 'ru' ? 'Ошибка при загрузке данных статьи' : 'Failed to load article data',
        );
      } finally {
        setLoading(false);
      }
    };
    fetchArticleData();
  }, [festschrift_id, section_id, article_id, lang]);

  // Заголовок статьи
  const getArticleTitle = (titleObj) => {
    if (!titleObj) return '';
    return getLocalizedName(titleObj);
  };

  // Полное имя (автор или рецензент)
  const getFullName = (fullNameObj) => {
    if (!fullNameObj) return '';
    const namePart = fullNameObj.name ? getLocalizedName(fullNameObj.name) : '';
    const surnamePart = fullNameObj.surname ? getLocalizedName(fullNameObj.surname) : '';
    return `${namePart} ${surnamePart}`.trim();
  };

  if (loading) {
    return (
      <div className="loading-state">
        {lang === 'ru' ? 'Загрузка данных статьи...' : 'Loading article data...'}
      </div>
    );
  }

  if (error) {
    return <div className="error-state">{error}</div>;
  }

  const { title, author, abstract, text } = articleData || {};

  const authorFullName = getFullName(author?.full_name);
  const isAuthorAnonymous = !authorFullName && !author?.email;

  const abstractObj = abstract || text || {};
  const articleAbstract =
    abstractObj && typeof abstractObj === 'object'
      ? abstractObj[lang] || abstractObj.en || abstractObj.ru || ''
      : '';

  return (
    <div className="thesis-management-layer">
      <div className="thesis-management-container">
        <div className="thesis-header">
          <p className="thesis-management-title">{getArticleTitle(title)}</p>
        </div>

        <div className="thesis-author-card">
          <h2>{lang === 'ru' ? 'Информация об авторе' : 'Author information'}</h2>

          {isAuthorAnonymous ? (
            <p>
              {lang === 'ru'
                ? 'Автор скрыт (анонимное рецензирование)'
                : 'Author is hidden (anonymous review)'}
            </p>
          ) : (
            <>
              <p>
                <strong>{lang === 'ru' ? 'ФИО:' : 'Full name:'}</strong> {authorFullName}
              </p>
              <p>
                <strong>Email:</strong> {author?.email}
              </p>
              {author?.phone && (
                <p>
                  <strong>{lang === 'ru' ? 'Телефон:' : 'Phone:'}</strong> {author.phone}
                </p>
              )}
              {author?.orcid && (
                <p>
                  <strong>ORCID:</strong> {author.orcid}
                </p>
              )}
              {author?.website && (
                <p>
                  <strong>{lang === 'ru' ? 'Веб-сайт:' : 'Website:'}</strong>{' '}
                  <a href={author.website} target="_blank" rel="noopener noreferrer">
                    {author.website}
                  </a>
                </p>
              )}
            </>
          )}
        </div>
        <div className="thesis-author-card">
          <h2>{lang === 'ru' ? 'Аннотация' : 'Abstract'}</h2>
          <p>{articleAbstract || (lang === 'ru' ? '—' : '—')}</p>
        </div>
      </div>
    </div>
  );
};

export default ThesisElectionPreviewPage;
